<template>
  <div class="calc-routes-control__container">
    <CollapseLateral
      id="lateral__collapse__user__layers"
      :disabled="userLayerCollapseButtonDisabled"
      class="mt-2"
      orientation="down-left"
      autoclose
      text-tooltip="Rutas"
      width="350px"
    >
      <template #icon>
        <span class="material-icons">route</span>
      </template>
      <template #title>
        <div
          class="calc-routes-control__routes-list-header"
        >
          <h4 class="calc-routes-control__title ml-2 my-1">
            Rutas
          </h4>
          <Button
            v-tooltip="routesVisibleTooltip"
            class="calc-routes-control__button mc-head__button"
            raised
            @click="toggleUserLayers"
          >
            <span class="material-icons">{{ routesVisibleIcon }}</span>
          </Button>
        </div>
      </template>
      <ul class="calc-routes-control__routes-list">
        <li
          v-for="(item) in props___.routeLayers"
          :key="item.id"
          class="calc-routes-control__route-info-container px-1 py-2"
        >
          <div class="calc-routes-control__route-info mb-2 mx-1">
            <span
              class="calc-routes-control__route-name"
            >{{ item.description ? item.description : 'sin nombre' }}</span>
          </div>
          <div class="w-full flex flex-row justify-content-between">
            <div :class="routeColorClass(item)"></div>
            <div
              class="calc-routes-control__button-container"
            >
              <Button
                v-tooltip="routeVisibleTooltip(item.id)"
                class="mc-row__button"
                raised
                rounded
                @click="toggleRoute(item.id)"
              >
                <span class="material-icons">{{ routeVisibleIcon(item.id) }}</span>
              </Button>
              <Button
                v-tooltip="'Enfocar ruta'"
                class="mc-row__button"
                :disabled="!isRouteVisible(item.id)"
                raised
                rounded
                @click="focusRoute(item.id)"
              >
                <span class="material-icons">location_on</span>
              </Button>
              <Button
                v-tooltip="'Eliminar ruta'"
                class="mc-row__button"
                raised
                rounded
                @click="removeRoute(item.id)"
              >
                <span class="pi pi-times"></span>
              </Button>
            </div>
          </div>
        </li>
      </ul>
    </CollapseLateral>
  </div>
</template>

<script setup>
import {computed, inject} from 'vue';
import {useStore} from 'vuex';
import {CollapseLateral} from '@sttedra_ui/framework';
// PrimeVue
import Button from 'primevue/button';
// #region Props, Emits

const props___ = defineProps({
  routeLayers: {
    type: Array,
    default: () => {
      return [];
    },
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const store = useStore();
const GROUP_ROUTE_LAYERS = inject('GROUP_ROUTE_LAYERS');

const userLayerCollapseButtonDisabled = computed(() => {
  return !props___?.routeLayers?.length;
});
const groupRouteUserLayers = computed(() => {
  const grul =
    GROUP_ROUTE_LAYERS?.getLayers()
      ?.getArray() ?? [];
  return grul;
});
const visibleRouteLayers = computed(() => {
  let allVisible = true;
  const guls = groupRouteUserLayers?.value ?? [];
  guls.forEach((rl) => {
    const layerVisible = rl.getVisible() ?? false;
    allVisible = allVisible && layerVisible;
  });
  return allVisible;
});
const routesVisibleIcon = computed(() => {
  const visible = visibleRouteLayers?.value ?? true;
  return visible ? 'directions_off' : 'directions';
});
const routesVisibleTooltip = computed(() => {
  const visible = visibleRouteLayers?.value ?? true;
  return visible ? 'Ocultar rutas calculadas' : 'Mostrar rutas calculadas';
});
// #endregion

// #region Functions
function routeColorClass(layer) {
  const baseClass = 'calc-routes-control__route-distinct-color bg-distinct-color';
  const colorIndex = layer?.colorIndex;
  if (colorIndex != null) {
    return baseClass + ' bg-distinct-color--' + (colorIndex + 1);
  }
  return baseClass + ' bg-distinct-color--1';
}
function toggleUserLayers() {
  const grul = groupRouteUserLayers?.value ?? [];
  const vrls = visibleRouteLayers?.value ?? false;
  grul.forEach((layer) => {
    layer.setVisible(!vrls);
  });
}
function isRouteVisible(routeId) {
  const layerArray = groupRouteUserLayers?.value ?? [];
  const layers = layerArray.filter((rl) => rl.get('id') === routeId);
  let visible = false;
  if (!!layers?.length) {
    for (const layer of layers) {
      visible = visible || (layer.getVisible() ?? false);
    }
  }
  return visible;
}
function routeVisibleIcon(routeId) {
  const isVisible = isRouteVisible(routeId);
  return isVisible ? 'directions_off' : 'directions';
}
function routeVisibleTooltip(routeId) {
  const isVisible = isRouteVisible(routeId);
  return isVisible ? 'Ocultar ruta' : 'Mostrar ruta';
}
async function focusRoute(routeId) {
  const selLayer = store.getters['userLayers/getRouteLayerById'](routeId);
  if (!!selLayer?.extent?.length) {
    const extent = selLayer.extent;
    await store.dispatch('map/setMapExtent', {extent}, {root: true});
  }
}
function toggleRoute(routeId) {
  const layerArray = groupRouteUserLayers?.value ?? [];
  const layers = layerArray.filter((rl) => rl.get('id') === routeId);
  if (!!layers?.length) {
    const oldVisible = isRouteVisible(routeId);
    for (const layer of layers) {
      layer.setVisible(!oldVisible);
    }
  }
}
async function removeRoute(routeId) {
  await store.dispatch('userLayers/removeRouteLayer', {id: routeId}, {root: true});
}
// #endregion

// #region Effects, Hooks

// #endregion

// #region Provide, Expose

// #endregion
</script>

<style lang="scss" scoped>
.calc-routes-control {
  &__container {
    & :deep(.lateral-collapse-container__contain) {
      padding: 0;
      transition: none;
    }
  }
  &__routes-list-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0.3rem;
    margin: 0.2rem 0.5rem;
    width: 100%;
  }
  &__button {
    padding: 0.2rem;
  }
  &__routes-list {
    border-top: 1px solid #a19f9d;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: 0.2rem 0.5rem;
    padding: 0.3rem;
  }
  &__route-info-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    /* padding: 0.5rem; */
    width: 100%;
    border-bottom: 1px solid #a19f9d;
    &:last-child {
      border-bottom: none;
    }
  }
  &__route-info {
    width: 100%;
  }
  &__route-name {
    max-width: 70%;
  }
  &__button-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 25%;
  }
  &__route-distinct-color {
    width: 100%;
    margin: 0 0.5rem;
  }
}
</style>
